import { Children, forwardRef } from 'react';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { Icon, Text, UnstyledButton } from '@myrealtrip/web-ui';

import TravelerAgeSelector from './TravelerAgeSelector';

interface Props {
  travelers: number[];
  selectedTraveler?: { index: number; age: number } | null;
  viewSelectedTravelerAge: boolean;
  exposureAgeSelectView: () => void;
  updateTravelers: (travelers: number[]) => void;
  updateSelectedTraveler: (traveler: { index: number; age: number } | null) => void;
}

const TravelerAgeList = forwardRef<HTMLDivElement, Props>(
  (
    {
      travelers,
      selectedTraveler,
      viewSelectedTravelerAge,
      exposureAgeSelectView,
      updateTravelers,
      updateSelectedTraveler,
    }: Props,
    ref,
  ) => {
    const theme = useTheme();

    const handleClickTrigger = (index: number) => () => {
      updateSelectedTraveler({ index, age: travelers[index] });
      exposureAgeSelectView();
    };

    const handleClickOption = (index: number, age: number) => () => {
      updateTravelers(travelers.map((travelerAge, i) => (i === index ? age : travelerAge)));
    };

    const handleRemove = (index: number) => () => {
      updateTravelers(travelers.filter((_, i) => i !== index));
    };

    return (
      <TravelerConditionListSection>
        <TravelerConditionList>
          {Children.toArray(
            travelers.map((travelerAge, index) => (
              <TravelerConditionItem>
                <TravelerConditionItemContent>
                  <Text typography="paragraphBold15" color={theme.colors.gray.s1000}>
                    탑승객 {index + 1} 나이
                  </Text>
                  <TravelerAgeSelector
                    ref={ref}
                    index={index}
                    travelerAge={travelerAge}
                    selectedTraveler={selectedTraveler}
                    viewSelectedTravelerAge={viewSelectedTravelerAge}
                    onClickTrigger={handleClickTrigger}
                    onClickOption={handleClickOption}
                  />
                </TravelerConditionItemContent>
                <ClearItemButton type="button" onClick={handleRemove(index)}>
                  <Icon name="IcoClear" size={20} color={theme.colors.gray.s500} opacity={0.5} />
                </ClearItemButton>
              </TravelerConditionItem>
            )),
          )}
        </TravelerConditionList>
      </TravelerConditionListSection>
    );
  },
);

TravelerAgeList.displayName = 'TravelerAgeList';

const TravelerConditionListSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const TravelerConditionList = styled.ul`
  display: flex;
  flex-direction: column;
`;

const TravelerConditionItem = styled.li`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 7px 20px;
  background: ${({ theme: { palettes } }) => palettes.gray[60]};
`;

const TravelerConditionItemContent = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 3px;
`;

const ClearItemButton = styled(UnstyledButton)`
  width: 20px;
  height: 20px;
  margin-left: 9.5px;
`;

export default TravelerAgeList;
